import { ThemeEvents } from '@theme/events';

class DeliveryEstimation extends HTMLElement {
  constructor() {
    super();

    this.minDateElement = null;
    this.maxDateElement = null;
    this.singleDateElement = null;
    this.container = null;

    this.config = {
      locale: 'fr',
      minDays: 3,
      maxDays: 5,
      noSaturday: false,
      noSunday: false,
      dateFormat: 'in_letters',
      showWeekday: true,
      cutoffHour: null,
      useSectionSettings: false,
      sectionConfig: null,
      todayLabel: "aujourd'hui",
      tomorrowLabel: 'demain',
      datePrefix: 'le ',
    };

    this.handleSubscriptionUpdated = () => this.#syncPurchaseOptionVisibility();
  }

  connectedCallback() {
    this.minDateElement = this.querySelector('[data-ref="min-date"]');
    this.maxDateElement = this.querySelector('[data-ref="max-date"]');
    this.singleDateElement = this.querySelector('[data-ref="single-date"]');
    this.datePrefixElement = this.querySelector('[data-ref="date-prefix"]');
    this.container = this.closest('.delivery-estimation');

    this.#loadConfiguration();
    this.#syncPurchaseOptionVisibility();
    // Re-sync après l'init des composants abonnement (ordre d'upgrade des custom elements)
    requestAnimationFrame(() => this.#syncPurchaseOptionVisibility());
    document.addEventListener(ThemeEvents.subscriptionUpdated, this.handleSubscriptionUpdated);

    if (this.config.useSectionSettings && !this.config.sectionConfig) {
      setTimeout(() => {
        this.#loadConfiguration();
        this.#calculateAndDisplayDates();
      }, 100);
    } else {
      this.#calculateAndDisplayDates();
    }
  }

  disconnectedCallback() {
    document.removeEventListener(ThemeEvents.subscriptionUpdated, this.handleSubscriptionUpdated);
  }

  /**
   * Affiche ou masque le bloc selon l'option d'achat sélectionnée.
   */
  #syncPurchaseOptionVisibility() {
    if (!this.container) return;

    const showFor = this.container.dataset.showForPurchaseOption || 'all';
    if (showFor === 'all') {
      this.container.dataset.purchaseHidden = 'false';
      return;
    }

    const purchaseOption = this.#getSelectedPurchaseOption();
    this.container.dataset.purchaseHidden = purchaseOption === showFor ? 'false' : 'true';
  }

  /**
   * Récupère l'option d'achat active (abonnement / achat unique).
   * @returns {'subscription' | 'one_time'}
   */
  #getSelectedPurchaseOption() {
    const hosts = document.querySelectorAll('subscription-component, subscription-v2-component');

    for (const host of hosts) {
      if (!this.#isVisible(host)) continue;
      if (typeof host.getSelectedOptionType === 'function') {
        return host.getSelectedOptionType();
      }
    }

    const sellingPlanInput = document.querySelector('input[name="selling_plan"]');
    if (sellingPlanInput?.value) return 'subscription';

    return 'one_time';
  }

  /**
   * @param {HTMLElement} element
   * @returns {boolean}
   */
  #isVisible(element) {
    let node = element;
    while (node && node !== document.body) {
      const style = getComputedStyle(node);
      if (style.display === 'none' || style.visibility === 'hidden') return false;
      node = node.parentElement;
    }
    return true;
  }

  #loadConfiguration() {
    this.config.locale = this.dataset.locale || 'fr';
    this.config.useSectionSettings = this.dataset.useSectionSettings === 'true';
    this.config.dateFormat = this.dataset.dateFormat || 'in_letters';
    this.config.showWeekday = this.dataset.showWeekday === 'true';
    this.config.todayLabel = this.dataset.labelToday ?? this.config.todayLabel;
    this.config.tomorrowLabel = this.dataset.labelTomorrow ?? this.config.tomorrowLabel;
    this.config.datePrefix = this.dataset.labelDatePrefix ?? this.config.datePrefix;

    if (this.config.useSectionSettings) {
      const configScripts = document.querySelectorAll('script[type="application/json"][id^="delivery-date-config-"]');
      
      if (configScripts.length > 0) {
        const configScript = configScripts[0];
        try {
          this.config.sectionConfig = JSON.parse(configScript.textContent);
          const scriptId = configScript.id;
          const sectionId = scriptId.replace('delivery-date-config-', '');
          this.config.sectionId = sectionId;
        } catch (e) {
          console.error('Erreur lors de l\'analyse de la configuration de date de livraison:', e);
          this.config.useSectionSettings = false;
        }
      } else {
        console.warn('Section Delivery Date Config introuvable. Assurez-vous que la section est ajoutée à votre thème.');
        this.config.useSectionSettings = false;
      }
    } else {
      this.config.minDays = parseInt(this.dataset.minDays) || 3;
      this.config.maxDays = parseInt(this.dataset.maxDays) || 5;
      this.config.noSaturday = this.dataset.noSaturday === 'true';
      this.config.noSunday = this.dataset.noSunday === 'true';

      const cutoffHour = this.dataset.cutoffHour;
      if (cutoffHour && cutoffHour !== '') {
        this.config.cutoffHour = parseInt(cutoffHour);
      }
    }
  }

  #calculateAndDisplayDates() {
    if (this.config.useSectionSettings && this.config.sectionConfig) {
      const dates = this.#calculateDeliveryDateFromSection();
      const singleDate = dates.minDate;
      
      if (this.singleDateElement) {
        let displayText;
        let isActualDate = false;
        
        if (singleDate._isZeroDays) {
          displayText = this.config.todayLabel;
        } else {
          displayText = this.#formatDate(singleDate, true);
          isActualDate = true;
        }
        
        this.singleDateElement.textContent = displayText;
        
        if (this.datePrefixElement) {
          if (isActualDate) {
            this.datePrefixElement.textContent = this.config.datePrefix;
          } else {
            this.datePrefixElement.textContent = '';
          }
        }
      }
      
      if (this.minDateElement) {
        this.minDateElement.textContent = '';
        this.minDateElement.style.display = 'none';
      }
      if (this.maxDateElement) {
        this.maxDateElement.textContent = '';
        this.maxDateElement.style.display = 'none';
      }
    } else {
      const minDate = this.#calculateDeliveryDate(this.config.minDays);
      const maxDate = this.#calculateDeliveryDate(this.config.maxDays);

      if (this.minDateElement) {
        this.minDateElement.textContent = this.#formatDate(minDate);
        this.minDateElement.style.display = '';
      }

      if (this.maxDateElement) {
        this.maxDateElement.textContent = this.#formatDate(maxDate);
        this.maxDateElement.style.display = '';
      }
      
      if (this.singleDateElement) {
        this.singleDateElement.textContent = '';
        this.singleDateElement.style.display = 'none';
      }
      
      if (this.datePrefixElement) {
        this.datePrefixElement.textContent = '';
      }
    }
  }

  #calculateDeliveryDateFromSection() {
    const now = new Date();
    const jsDayIndex = now.getDay();
    const todayIndex = jsDayIndex === 0 ? 7 : jsDayIndex;

    const config = this.config.sectionConfig;
    if (!config) {
      return {
        minDate: this.#calculateDeliveryDate(3),
        maxDate: this.#calculateDeliveryDate(3),
      };
    }

    const shiftTimeArray = config.shiftTimeArray || [];
    const morningShiftDays = config.morningShiftDays || [];
    const dayShiftDays = config.dayShiftDays || [];
    const shiftHours1st = config.shiftHours1st || [];

    const shiftIndexStr = shiftTimeArray[todayIndex] || '3';
    const shiftIndex = parseInt(shiftIndexStr) || 3;

    const cutoffHour1stStr = shiftHours1st[shiftIndex] || '13';
    const cutoffHour1st = parseInt(cutoffHour1stStr) || 13;

    const currentHour = now.getHours();
    const currentMin = now.getMinutes();
    const totalMinutes = currentHour * 60 + currentMin;
    const cutoffMinutes1st = cutoffHour1st * 60;

    let deliveryDays;
    if (totalMinutes < cutoffMinutes1st) {
      const morningDaysStr = morningShiftDays[todayIndex] || '3';
      const parsedDays = parseInt(morningDaysStr);
      deliveryDays = isNaN(parsedDays) ? 3 : parsedDays;
    } else {
      const dayDaysStr = dayShiftDays[todayIndex] || '3';
      const parsedDays = parseInt(dayDaysStr);
      deliveryDays = isNaN(parsedDays) ? 3 : parsedDays;
    }

    if (deliveryDays === 0) {
      const todayDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      
      Object.defineProperty(todayDate, '_isZeroDays', {
        value: true,
        writable: false,
        enumerable: false,
        configurable: false
      });
      
      return {
        minDate: todayDate,
        maxDate: todayDate,
      };
    }

    const secsInDay = 24 * 60 * 60;
    const tillTodaySecs = Math.floor(now.getTime() / 1000);
    const secsToAdd = secsInDay * deliveryDays;
    const deliveryTimestamp = tillTodaySecs + secsToAdd;
    let deliveryDate = new Date(deliveryTimestamp * 1000);
    
    deliveryDate = new Date(deliveryDate.getFullYear(), deliveryDate.getMonth(), deliveryDate.getDate());

    while (this.#isNonDeliveryDayForSection(deliveryDate)) {
      deliveryDate.setDate(deliveryDate.getDate() + 1);
    }
    
    deliveryDate = new Date(deliveryDate.getFullYear(), deliveryDate.getMonth(), deliveryDate.getDate());

    return {
      minDate: deliveryDate,
      maxDate: deliveryDate,
    };
  }

  #isNonDeliveryDayForSection(date) {
    const dayOfWeek = date.getDay();
    const jsDayIndex = dayOfWeek === 0 ? 7 : dayOfWeek;
    
    const config = this.config.sectionConfig;
    if (!config) {
      return dayOfWeek === 6 || dayOfWeek === 0;
    }
    
    const morningShiftDays = config.morningShiftDays || [];
    const dayShiftDays = config.dayShiftDays || [];
    
    if (dayOfWeek === 6) {
      const saturdayIndex = 6;
      const hasMorningConfig = morningShiftDays[saturdayIndex] !== undefined && morningShiftDays[saturdayIndex] !== '';
      const hasDayConfig = dayShiftDays[saturdayIndex] !== undefined && dayShiftDays[saturdayIndex] !== '';
      if (hasMorningConfig || hasDayConfig) {
        return false;
      }
    } else if (dayOfWeek === 0) {
      const sundayIndex = 7;
      const hasMorningConfig = morningShiftDays[sundayIndex] !== undefined && morningShiftDays[sundayIndex] !== '';
      const hasDayConfig = dayShiftDays[sundayIndex] !== undefined && dayShiftDays[sundayIndex] !== '';
      if (hasMorningConfig || hasDayConfig) {
        return false;
      }
    }
    
    return dayOfWeek === 6 || dayOfWeek === 0;
  }

  #calculateDeliveryDate(daysToAdd) {
    const now = new Date();
    let startDate = new Date();

    if (daysToAdd === 0) {
      return new Date(now.getFullYear(), now.getMonth(), now.getDate());
    }

    if (this.config.cutoffHour !== null) {
      const currentHour = now.getHours();
      if (currentHour >= this.config.cutoffHour) {
        startDate.setDate(startDate.getDate() + 1);
      }
    }

    let businessDaysAdded = 0;
    let currentDate = new Date(startDate);

    while (businessDaysAdded < daysToAdd) {
      currentDate.setDate(currentDate.getDate() + 1);

      const dayOfWeek = currentDate.getDay();
      const isSaturday = dayOfWeek === 6;
      const isSunday = dayOfWeek === 0;

      const isBusinessDay = !((isSaturday && this.config.noSaturday) || (isSunday && this.config.noSunday));

      if (isBusinessDay) {
        businessDaysAdded++;
      }
    }

    while (this.#isNonDeliveryDay(currentDate)) {
      currentDate.setDate(currentDate.getDate() + 1);
    }
    
    return new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate());
  }

  #isNonDeliveryDay(date) {
    const dayOfWeek = date.getDay();
    const isSaturday = dayOfWeek === 6;
    const isSunday = dayOfWeek === 0;

    return (isSaturday && this.config.noSaturday) || (isSunday && this.config.noSunday);
  }

  #formatDate(date, useSectionSettings = false) {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    const dateToCompare = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    
    const isToday = dateToCompare.getTime() === today.getTime();
    const isTomorrow = dateToCompare.getTime() === tomorrow.getTime();
    
    if (date._isZeroDays) {
      return this.config.todayLabel;
    }
    
    if (isToday && useSectionSettings && this.config.sectionConfig && this.config.sectionConfig.showToday) {
      return this.config.todayLabel;
    }
    
    if (isTomorrow && useSectionSettings && this.config.sectionConfig && this.config.sectionConfig.showTomorrow) {
      return this.config.tomorrowLabel;
    }
    
    const options = {};

    if (this.config.dateFormat === 'numbers') {
      options.day = 'numeric';
      options.month = 'numeric';
    } else {
      options.day = 'numeric';
      options.month = 'long';
    }

    if (this.config.showWeekday) {
      options.weekday = 'long';
    }

    const formatter = new Intl.DateTimeFormat(this.config.locale, options);

    let formattedDate = formatter.format(date);

    formattedDate = this.#capitalizeFirstLetter(formattedDate);

    return formattedDate;
  }

  #capitalizeFirstLetter(str) {
    if (!str) return str;
    return str.charAt(0).toUpperCase() + str.slice(1);
  }
}

if (!customElements.get('delivery-estimation')) {
  customElements.define('delivery-estimation', DeliveryEstimation);
}
